import React, { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  Badge,
  Modal,
  Form,
  FormField,
  Input,
  Select,
  Alert,
  StatusIndicator,
  ColumnLayout
} from '@cloudscape-design/components'
import { useMutation, useQuery, useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAmplifyAuth } from '../../../providers/AmplifyAuthProvider'
import { usePageHeader } from '../../../providers/PageHeaderProvider'
import KpiCardGrid from '../../../shared/components/KpiCardGrid'
import SelectableTable from '../../../shared/components/SelectableTable'
import type { Id } from '../../../../convex/_generated/dataModel'

interface UserFormData {
  email: string
  name: string
  role: 'operator' | 'supervisor' | 'manager' | 'engineer' | 'admin'
  phone?: string
  department?: string
  location?: string
}

const USER_ROLES = [
  { value: 'operator', label: 'Operator', description: 'Equipment operation and data entry' },
  { value: 'supervisor', label: 'Supervisor', description: 'Production line oversight' },
  { value: 'manager', label: 'Manager', description: 'Factory management and reporting' },
  { value: 'engineer', label: 'Engineer', description: 'Technical configuration and maintenance' },
  { value: 'admin', label: 'Administrator', description: 'Full system access' }
]

const ROLE_PERMISSIONS = {
  operator: ['assigned_assets', 'production_lots', 'alerts'],
  supervisor: ['production_line_assets', 'all_operators', 'reports'],
  manager: ['all_factory_assets', 'analytics', 'costs'],
  engineer: ['all_assets', 'spc_limits', 'maintenance_history'],
  admin: ['*']
}

export default function UserManager() {
  const { sessionId, user: currentUser } = useAmplifyAuth()
  const { setHeaderConfig } = usePageHeader()
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<any[]>([])
  const [showUserDetails, setShowUserDetails] = useState(false)
  const [showEditMode, setShowEditMode] = useState(false)
  const [isEditingUser, setIsEditingUser] = useState(false)
  const [editUserError, setEditUserError] = useState<string | null>(null)
  const [isCreatingUser, setIsCreatingUser] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [createdUserInfo, setCreatedUserInfo] = useState<{name: string, email: string, temporaryPassword: string} | null>(null)
  const [createUserError, setCreateUserError] = useState<string | null>(null)
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    name: '',
    role: 'operator',
    phone: '',
    department: '',
    location: ''
  })

  // Set page header
  useEffect(() => {
    setHeaderConfig({
      title: 'User Management',
      description: 'Manage users within your organization. Invite new users, assign roles, and configure access permissions.',
      actions: [
        {
          id: 'invite-user',
          component: (
            <Button 
              variant="primary" 
              onClick={() => setShowInviteModal(true)}
            >
              Invite User
            </Button>
          )
        }
      ]
    })
  }, []) // Remove setHeaderConfig from dependencies to prevent infinite loop

  // Queries - using component structure
  const users = useQuery(api.components.auth.index.getUsersForTenant, 
    sessionId ? { sessionId } : 'skip'
  )

  // Mutations and Actions
  const updateUserRole = useMutation(api.components.auth.index.updateUserRole)
  const updateAnyUserProfile = useMutation(api.components.auth.index.updateAnyUserProfile)
  const updateUserInCognito = useAction(api.components.auth.cognitoSync.updateUserInCognito)
  const createUser = useMutation(api.components.auth.index.createUser)
  const syncUserToCognito = useAction(api.components.auth.cognitoSync.syncUserToCognito)

  const handleInviteUser = async () => {
    if (!sessionId) {
      setCreateUserError('No session available')
      return
    }

    setIsCreatingUser(true)
    setCreateUserError(null)

    try {
      console.log('Creating user:', formData)
      
      // Step 1: Create user in Convex database
      const newUser = await createUser({
        sessionId,
        email: formData.email,
        name: formData.name,
        role: formData.role,
        phone: formData.phone,
        department: formData.department,
        location: formData.location
      })

      console.log('User created in Convex:', newUser)
      
      // Step 2: Create user in Cognito and sync
      const tempPassword = `TempPass123!${Date.now().toString().slice(-4)}`
      let cognitoSuccess = false
      
      try {
        const cognitoResult = await syncUserToCognito({
          sessionId,
          userId: newUser._id,
          email: formData.email,
          name: formData.name,
          role: formData.role,
          temporaryPassword: tempPassword
        })
        
        console.log('User synced to Cognito:', cognitoResult)
        cognitoSuccess = true
        
        // Store info for success modal
        setCreatedUserInfo({
          name: formData.name,
          email: formData.email,
          temporaryPassword: tempPassword
        })
        
      } catch (cognitoError) {
        console.warn('Failed to sync to Cognito (but user exists in database):', cognitoError)
        // Don't fail the entire operation if Cognito sync fails
      }
      
      // Close invite modal and reset form
      setShowInviteModal(false)
      resetForm()
      
      // Show success modal with temp password (only if Cognito sync succeeded)
      if (cognitoSuccess) {
        setShowSuccessModal(true)
      }
      setCreateUserError(null)
      
      console.log('User invitation process completed successfully')
      
    } catch (error: any) {
      console.error('Failed to create user:', error)
      setCreateUserError(error.message || 'Failed to create user')
    } finally {
      setIsCreatingUser(false)
    }
  }

  const handleUpdateRole = async (userId: Id<'users'>, newRole: any) => {
    if (!sessionId) return
    try {
      await updateUserRole({
        sessionId,
        userId,
        newRole
      })
    } catch (error) {
      console.error('Failed to update user role:', error)
    }
  }

  const handleEditUser = async () => {
    if (!sessionId || !selectedUser) {
      setEditUserError('No session or user selected')
      return
    }

    setIsEditingUser(true)
    setEditUserError(null)

    try {
      console.log('Updating user profile:', {
        userId: selectedUser._id,
        updates: {
          name: formData.name,
          phone: formData.phone,
          department: formData.department,
          location: formData.location
        }
      })

      // Step 1: Update in Convex database
      await updateAnyUserProfile({
        sessionId,
        userId: selectedUser._id,
        updates: {
          name: formData.name,
          phone: formData.phone,
          department: formData.department,
          location: formData.location
        }
      })

      // Step 2: Update role if changed
      if (formData.role !== selectedUser.role) {
        await updateUserRole({
          sessionId,
          userId: selectedUser._id,
          newRole: formData.role
        })
      }

      // Step 3: Sync with Cognito (name and phone)
      try {
        const cognitoResult = await updateUserInCognito({
          sessionId,
          userId: selectedUser._id,
          updates: {
            name: formData.name,
            phone: formData.phone
          }
        })
        console.log('Cognito sync result:', cognitoResult)
        
        if (!cognitoResult.success) {
          // Show warning but don't fail the operation
          console.warn('Cognito sync partially failed:', cognitoResult.message)
          if (cognitoResult.error?.includes('phone number format')) {
            // Could show a toast notification here about phone format
            console.warn('Phone number format issue - database updated but Cognito sync skipped')
          }
        }
      } catch (cognitoError) {
        console.warn('Failed to sync with Cognito (but database updated):', cognitoError)
        // Don't fail the entire operation if Cognito sync fails
      }

      setIsEditingUser(false)
      setShowEditMode(false)
      setShowUserDetails(false)
      resetForm()
    } catch (error: any) {
      console.error('Failed to update user:', error)
      setEditUserError(error.message || 'Failed to update user')
      setIsEditingUser(false)
    }
  }

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      role: 'operator',
      phone: '',
      department: '',
      location: ''
    })
    setCreateUserError(null)
    setEditUserError(null)
  }

  const populateFormForEdit = (user: any) => {
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      phone: user.phone || '',
      department: user.department || '',
      location: user.location || ''
    })
  }

  // KPI data
  const kpiData = useMemo(() => [
    {
      id: 'active-users',
      title: 'Active Users',
      value: users?.filter(u => u.status === 'active').length || 0
    },
    {
      id: 'pending-invitations', 
      title: 'Pending Invitations',
      value: users?.filter(u => u.status === 'pending_invitation').length || 0
    },
    {
      id: 'administrators',
      title: 'Administrators', 
      value: users?.filter(u => u.role === 'admin').length || 0
    },
    {
      id: 'total-users',
      title: 'Total Users',
      value: users?.length || 0
    }
  ], [users])

  const userTableItems = useMemo(() => {
    if (!users) return []
    
    return users.map(user => ({
      ...user,
      statusBadge: (
        <StatusIndicator 
          type={
            user.status === 'active' ? 'success' : 
            user.status === 'pending_invitation' ? 'pending' : 'stopped'
          }
        >
          {user.status.replace('_', ' ')}
        </StatusIndicator>
      ),
      roleBadge: (
        <Badge 
          color={
            user.role === 'admin' ? 'red' :
            user.role === 'manager' ? 'blue' :
            user.role === 'engineer' ? 'green' :
            user.role === 'supervisor' ? 'yellow' : 'grey'
          }
        >
          {user.role}
        </Badge>
      ),
      lastLoginDate: user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never',
      createdDate: new Date(user.createdAt).toLocaleDateString()
    }))
  }, [users])

  const userTableColumns = [
    {
      id: 'name',
      header: 'Name',
      cell: (item: any) => item.name,
      sortingField: 'name'
    },
    {
      id: 'email',
      header: 'Email',
      cell: (item: any) => item.email,
      sortingField: 'email'
    },
    {
      id: 'role',
      header: 'Role',
      cell: (item: any) => item.roleBadge
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: any) => item.statusBadge
    },
    {
      id: 'department',
      header: 'Department',
      cell: (item: any) => item.department || '-'
    },
    {
      id: 'lastLogin',
      header: 'Last Login',
      cell: (item: any) => item.lastLoginDate,
      sortingField: 'lastLoginAt'
    },
    {
      id: 'createdDate',
      header: 'Created',
      cell: (item: any) => item.createdDate,
      sortingField: 'createdAt'
    }
  ]

  const selectedUser = selectedUsers[0] // Single selection
  const selectedRole = USER_ROLES.find(r => r.value === formData.role)

  // Table actions based on selection
  const tableActions = (
    <SpaceBetween direction="horizontal" size="xs">
      <Button
        disabled={selectedUsers.length !== 1}
        onClick={() => {
          if (selectedUsers.length === 1) {
            setShowUserDetails(true)
            setShowEditMode(false) // Ensure we start in view mode
          }
        }}
      >
        View details
      </Button>
      <Button
        disabled={selectedUsers.length !== 1 || !currentUser || currentUser.role !== 'admin'}
        onClick={() => {
          if (selectedUsers.length === 1) {
            populateFormForEdit(selectedUsers[0])
            setShowUserDetails(true)
            setShowEditMode(true)
          }
        }}
      >
        Edit
      </Button>
      {currentUser?.role === 'admin' && (
        <Button
          variant="normal"
          disabled={selectedUsers.length === 0 || selectedUsers.some(user => user.role === 'admin')}
          onClick={() => {
            // Quick role change could go here
          }}
        >
          Change role
        </Button>
      )}
    </SpaceBetween>
  )

  return (
    <SpaceBetween size="l">
      {/* KPI Cards */}
      <KpiCardGrid 
        items={kpiData}
        loading={!users}
        columns={4}
      />

      {/* Users Table */}
      <SelectableTable
        items={userTableItems}
        columnDefinitions={userTableColumns}
        loading={!users}
        loadingText="Loading users..."
        header="Users"
        selectionType="single"
        selectedItems={selectedUsers}
        onSelectionChange={setSelectedUsers}
        actions={tableActions}
        filter={{
          placeholder: "Search users by name, email, role..."
        }}
        pagination={{
          pageSize: 20
        }}
        preferences={{
          title: "Table preferences",
          pageSizeOptions: [
            { value: 10, label: "10 users" },
            { value: 20, label: "20 users" },
            { value: 50, label: "50 users" }
          ]
        }}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No users</b>
            <Box variant="p" color="inherit">
              No users to display.
            </Box>
          </Box>
        }
      />

      {/* User Details/Edit Modal */}
      {selectedUser && (
        <Modal
          visible={showUserDetails}
          onDismiss={() => {
            setShowUserDetails(false)
            setShowEditMode(false)
            setIsEditingUser(false)
            setEditUserError(null)
            resetForm()
          }}
          header={showEditMode ? `Edit User: ${selectedUser.name}` : `User Details: ${selectedUser.name}`}
          size="large"
          footer={
            showEditMode ? (
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button 
                    onClick={() => {
                      setShowEditMode(false)
                      setIsEditingUser(false)
                      setEditUserError(null)
                      // Don't call resetForm() to keep the view showing
                    }}
                    disabled={isEditingUser} // Disable only when actually saving
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleEditUser}
                    disabled={!formData.name || isEditingUser}
                    loading={isEditingUser}
                  >
                    {isEditingUser ? 'Saving...' : 'Save Changes'}
                  </Button>
                </SpaceBetween>
              </Box>
            ) : (
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => setShowUserDetails(false)}>
                    Close
                  </Button>
                  {currentUser?.role === 'admin' && selectedUser.role !== 'admin' && (
                    <Button
                      variant="primary"
                      onClick={() => {
                        populateFormForEdit(selectedUser)
                        setShowEditMode(true)
                      }}
                    >
                      Edit User
                    </Button>
                  )}
                </SpaceBetween>
              </Box>
            )
          }
        >
          {showEditMode ? (
            // Edit Form
            <Form>
              {editUserError && (
                <Alert type="error" header="Failed to update user">
                  {editUserError}
                </Alert>
              )}
              <SpaceBetween size="l">
                <ColumnLayout columns={2}>
                  <FormField label="Email Address" description="Cannot be changed">
                    <Input
                      value={formData.email}
                      disabled={true}
                      placeholder="user@company.com"
                      type="email"
                    />
                  </FormField>

                  <FormField label="Full Name" description="User's display name">
                    <Input
                      value={formData.name}
                      onChange={({ detail }) =>
                        setFormData(prev => ({ ...prev, name: detail.value }))
                      }
                      placeholder="John Smith"
                    />
                  </FormField>
                </ColumnLayout>

                <FormField label="Role" description={selectedRole?.description}>
                  <Select
                    selectedOption={{ 
                      value: formData.role, 
                      label: selectedRole?.label || formData.role 
                    }}
                    onChange={({ detail }) =>
                      setFormData(prev => ({ ...prev, role: detail.selectedOption.value as any }))
                    }
                    options={USER_ROLES}
                  />
                </FormField>

                <ColumnLayout columns={3}>
                  <FormField 
                    label="Phone" 
                    description="Optional. Format: +1 555-123-4567 (will be auto-formatted for Cognito)"
                  >
                    <Input
                      value={formData.phone || ''}
                      onChange={({ detail }) =>
                        setFormData(prev => ({ ...prev, phone: detail.value }))
                      }
                      placeholder="+1 555-123-4567"
                      type="tel"
                    />
                  </FormField>

                  <FormField label="Department" description="Optional">
                    <Input
                      value={formData.department || ''}
                      onChange={({ detail }) =>
                        setFormData(prev => ({ ...prev, department: detail.value }))
                      }
                      placeholder="Manufacturing"
                    />
                  </FormField>

                  <FormField label="Location" description="Optional">
                    <Input
                      value={formData.location || ''}
                      onChange={({ detail }) =>
                        setFormData(prev => ({ ...prev, location: detail.value }))
                      }
                      placeholder="Building A"
                    />
                  </FormField>
                </ColumnLayout>
              </SpaceBetween>
            </Form>
          ) : (
            // View Details
            <SpaceBetween size="l">
              {/* Basic Info */}
              <Container header={<Header variant="h3">Basic Information</Header>}>
                <ColumnLayout columns={2}>
                  <div key="name">
                    <Box variant="awsui-key-label">Name</Box>
                    <div>{selectedUser.name}</div>
                  </div>
                  <div key="email">
                    <Box variant="awsui-key-label">Email</Box>
                    <div>{selectedUser.email}</div>
                  </div>
                  <div key="role">
                    <Box variant="awsui-key-label">Role</Box>
                    <Badge color={
                      selectedUser.role === 'admin' ? 'red' :
                      selectedUser.role === 'manager' ? 'blue' :
                      selectedUser.role === 'engineer' ? 'green' :
                      selectedUser.role === 'supervisor' ? 'yellow' : 'grey'
                    }>
                      {selectedUser.role}
                    </Badge>
                  </div>
                  <div key="status">
                    <Box variant="awsui-key-label">Status</Box>
                    <StatusIndicator 
                      type={selectedUser.status === 'active' ? 'success' : 'pending'}
                    >
                      {selectedUser.status.replace('_', ' ')}
                    </StatusIndicator>
                  </div>
                  <div key="phone">
                    <Box variant="awsui-key-label">Phone</Box>
                    <div>{selectedUser.phone || '-'}</div>
                  </div>
                  <div key="department">
                    <Box variant="awsui-key-label">Department</Box>
                    <div>{selectedUser.department || '-'}</div>
                  </div>
                  <div key="location">
                    <Box variant="awsui-key-label">Location</Box>
                    <div>{selectedUser.location || '-'}</div>
                  </div>
                  <div key="lastLogin">
                    <Box variant="awsui-key-label">Last Login</Box>
                    <div>{selectedUser.lastLoginDate}</div>
                  </div>
                  <div key="created">
                    <Box variant="awsui-key-label">Created</Box>
                    <div>{selectedUser.createdDate}</div>
                  </div>
                </ColumnLayout>
              </Container>

              {/* Permissions */}
              <Container header={<Header variant="h3">Role Permissions</Header>}>
                <SpaceBetween direction="horizontal" size="xs">
                  {ROLE_PERMISSIONS[selectedUser.role]?.map(permission => (
                    <Badge key={permission} color="blue">
                      {permission}
                    </Badge>
                  )) || <Badge color="red">All permissions</Badge>}
                </SpaceBetween>
              </Container>
            </SpaceBetween>
          )}
        </Modal>
      )}

      {/* Invite User Modal */}
      <Modal
        visible={showInviteModal}
        onDismiss={() => {
          if (!isCreatingUser) {
            setShowInviteModal(false)
            resetForm()
          }
        }}
        header="Invite New User"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                onClick={() => {
                  setShowInviteModal(false)
                  resetForm()
                }}
                disabled={isCreatingUser}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleInviteUser}
                disabled={!formData.email || !formData.name || isCreatingUser}
                loading={isCreatingUser}
              >
                {isCreatingUser ? 'Creating User...' : 'Create User'}
              </Button>
            </SpaceBetween>
          </Box>
        }
        size="large"
      >
        <Form>
          {createUserError && (
            <Alert type="error" header="Failed to create user">
              {createUserError}
            </Alert>
          )}
          <SpaceBetween size="l">
            <ColumnLayout columns={2}>
              <FormField label="Email Address" description="User's email for invitation">
                <Input
                  value={formData.email}
                  onChange={({ detail }) => 
                    setFormData(prev => ({ ...prev, email: detail.value }))
                  }
                  placeholder="user@company.com"
                  type="email"
                />
              </FormField>

              <FormField label="Full Name" description="User's display name">
                <Input
                  value={formData.name}
                  onChange={({ detail }) =>
                    setFormData(prev => ({ ...prev, name: detail.value }))
                  }
                  placeholder="John Smith"
                />
              </FormField>
            </ColumnLayout>

            <FormField label="Role" description={selectedRole?.description}>
              <Select
                selectedOption={{ 
                  value: formData.role, 
                  label: selectedRole?.label || formData.role 
                }}
                onChange={({ detail }) =>
                  setFormData(prev => ({ ...prev, role: detail.selectedOption.value as any }))
                }
                options={USER_ROLES}
              />
            </FormField>

            <ColumnLayout columns={3}>
              <FormField 
                label="Phone" 
                description="Optional. Format: +1 555-123-4567 (will be auto-formatted for Cognito)"
              >
                <Input
                  value={formData.phone || ''}
                  onChange={({ detail }) =>
                    setFormData(prev => ({ ...prev, phone: detail.value }))
                  }
                  placeholder="+1 555-123-4567"
                  type="tel"
                />
              </FormField>

              <FormField label="Department" description="Optional">
                <Input
                  value={formData.department || ''}
                  onChange={({ detail }) =>
                    setFormData(prev => ({ ...prev, department: detail.value }))
                  }
                  placeholder="Manufacturing"
                />
              </FormField>

              <FormField label="Location" description="Optional">
                <Input
                  value={formData.location || ''}
                  onChange={({ detail }) =>
                    setFormData(prev => ({ ...prev, location: detail.value }))
                  }
                  placeholder="Building A"
                />
              </FormField>
            </ColumnLayout>

            {/* Permission Preview */}
            <Container header={<Header variant="h4">Permission Preview</Header>}>
              <Alert type="info">
                This user will have access to: {ROLE_PERMISSIONS[formData.role]?.join(', ') || 'All permissions'}
              </Alert>
            </Container>
          </SpaceBetween>
        </Form>
      </Modal>

      {/* Success Modal with Temporary Password */}
      <Modal
        onDismiss={() => setShowSuccessModal(false)}
        visible={showSuccessModal}
        size="medium"
        header="User Created Successfully"
      >
        <SpaceBetween direction="vertical" size="m">
          <Alert type="success">
            User has been successfully created in both the system and AWS Cognito.
          </Alert>
          
          <Container>
            <SpaceBetween direction="vertical" size="s">
              <div><strong>Name:</strong> {createdUserInfo?.name}</div>
              <div><strong>Email:</strong> {createdUserInfo?.email}</div>
              <div style={{background: '#f4f4f4', padding: '12px', borderRadius: '4px', fontFamily: 'monospace'}}>
                <strong>Temporary Password:</strong> <span style={{color: '#d91e18'}}>{createdUserInfo?.temporaryPassword}</span>
              </div>
            </SpaceBetween>
          </Container>
          
          <Alert type="warning">
            <strong>Important:</strong> Please share this temporary password with the user. 
            They will be required to change it on their first login.
          </Alert>
          
          <Box float="right">
            <Button 
              variant="primary" 
              onClick={() => {
                navigator.clipboard.writeText(createdUserInfo?.temporaryPassword || '')
                setShowSuccessModal(false)
              }}
            >
              Copy Password & Close
            </Button>
          </Box>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  )
}